import React, { useEffect, useRef, useState } from "react";
import VideoSkeletonLoader from "./VideoSkeletonLoader";

interface IframeVideoEmbedProps {
  src: string;
  title?: string;
  className?: string;
  style?: React.CSSProperties;
  onError?: () => void;
  onLoad?: () => void;
  loadTimeoutMs?: number;
}

const baseStyle: React.CSSProperties = {
  minHeight: "350px",
  border: "none",
};

const IframeVideoEmbed: React.FC<IframeVideoEmbedProps> = ({
  src,
  title,
  className = "w-100 h-100 rounded-4",
  style,
  onError,
  onLoad,
  loadTimeoutMs = 12000,
}) => {
  const hasLoadedRef = useRef(false);
  const timeoutRef = useRef<number>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    hasLoadedRef.current = false;
    setIsLoading(true);

    timeoutRef.current = window.setTimeout(() => {
      if (!hasLoadedRef.current) {
        onError?.();
      }
    }, loadTimeoutMs);

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [src, loadTimeoutMs, onError]);

  return (
    <div
      className="position-relative w-100"
      style={{ ...baseStyle, ...style }}
    >
      {isLoading && (
        <VideoSkeletonLoader
          className={`${className} position-absolute top-0 start-0`}
          style={style}
        />
      )}
      <iframe
        src={src}
        title={title || "Property video"}
        className={className}
        style={{
          ...baseStyle,
          ...style,
          opacity: isLoading ? 0 : 1,
          transition: "opacity 0.25s ease",
        }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        allowFullScreen
        onLoad={() => {
          hasLoadedRef.current = true;
          setIsLoading(false);
          onLoad?.();
          if (timeoutRef.current) {
            window.clearTimeout(timeoutRef.current);
          }
        }}
        onError={() => onError?.()}
      />
    </div>
  );
};

export default IframeVideoEmbed;
